import { Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { X } from 'lucide-react'
import clsx from 'clsx'

/**
 * Enhanced Base Modal component using @headlessui/react
 */
export default function Modal({
  open,
  onClose,
  title,
  children,
  size = 'md',  // sm | md | lg | xl | full
  footer,
  closable = true,
  className,
}) {
  const sizes = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-7xl mx-4',
  }

  const handleClose = () => {
    if (closable) onClose();
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                className={clsx(
                  'relative transform overflow-hidden rounded-2xl bg-white text-left shadow-2xl transition-all w-full flex flex-col',
                  sizes[size] || sizes.md,
                  className
                )}
              >
                {/* Header */}
                {(title || closable) && (
                  <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                    {title && (
                      <Dialog.Title as="h2" className="text-lg font-semibold text-gray-900">
                        {title}
                      </Dialog.Title>
                    )}
                    {closable && (
                      <button
                        type="button"
                        onClick={onClose}
                        className="ml-auto p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50"
                      >
                        <X size={18} />
                      </button>
                    )}
                  </div>
                )}

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-5">
                  {children}
                </div>

                {/* Footer */}
                {footer && (
                  <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex items-center justify-end gap-3">
                    {footer}
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}

/** Convenience wrapper with Cancel + Submit buttons */
export function ModalForm({
  open, onClose, title, onSubmit, loading, submitLabel = 'Сохранить',
  size = 'md', children, submitDanger = false, id = "modal-form"
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size={size}
      footer={
        <>
          <button
            type="button"
            className="btn-secondary"
            onClick={onClose}
            disabled={loading}
          >
            Отмена
          </button>
          <button
            type="submit"
            form={id}
            disabled={loading}
            className={submitDanger ? 'btn-danger' : 'btn-primary'}
          >
            {loading ? 'Сохранение...' : submitLabel}
          </button>
        </>
      }
    >
      <form id={id} onSubmit={onSubmit} className="space-y-4">
        {children}
      </form>
    </Modal>
  )
}
